import DOMPurify from 'dompurify';

import type { SearchHit } from './types';

/**
 * Maps a search hit to the dashboard route that displays it. Single source of
 * truth shared by the Cmd+K dialog and the AI search results page — keep the
 * two in sync by importing this rather than re-deriving paths.
 */
export function hitPath(hit: SearchHit): string | null {
  if (hit.entity_id == null) return null;
  switch (hit.entity_type) {
    case 'registration':
      return `/dashboard/containers/${hit.entity_id}`;
    case 'box':
      return `/dashboard/packing/${hit.entity_id}`;
    case 'shipment':
      return `/dashboard/shipping/${hit.entity_id}`;
    case 'page_index':
      return `/dashboard/sticking/${hit.entity_id}`;
    case 'ocr_result':
      // There is no `/dashboard/ocr/{id}` route — an OCR result is read on its
      // container. Without a parent id there is nowhere honest to send anyone.
      return hit.registration_id != null
        ? `/dashboard/containers/${hit.registration_id}`
        : null;
    case 'document_chunk':
      // No standalone page — deep-link to the parent registration if known.
      return hit.registration_id != null
        ? `/dashboard/containers/${hit.registration_id}`
        : null;
    case 'client':
      // Enterprise Clients has a listing and no detail page.
      return '/dashboard/clients';
    case 'ocr_template':
      return `/dashboard/ocr-templates#edit/${hit.entity_id}`;
    case 'dynamic_form':
      return `/dashboard/form-management#edit/${hit.entity_id}`;
    default:
      return null;
  }
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

/** Only the exact opening and closing tag, no attributes, either case. */
const HIGHLIGHT_TAG = /&lt;(\/?)mark&gt;/gi;

/**
 * Make a Typesense highlight snippet safe to render as HTML.
 *
 * Typesense wraps the matched term in `<mark>` but leaves the surrounding text
 * exactly as indexed — and what is indexed here is OCR output from
 * user-uploaded documents, so any markup a scanned page contains arrives
 * verbatim. Escaping everything and then re-admitting only the two highlight
 * tags is the whole safety property: an allowlist of two literal strings, with
 * no attributes and therefore no event handler, URL or style to carry anything
 * through.
 */
export function sanitizeSearchSnippet(snippet?: string | null): string {
  if (!snippet) {
    return '';
  }

  return snippet
    .replace(/[&<>"']/g, (char) => HTML_ESCAPES[char])
    .replace(HIGHLIGHT_TAG, '<$1mark>');
}

/** The whole allowlist: the highlight element, and not one attribute on it. */
const SNIPPET_PURIFY_CONFIG = { ALLOWED_TAGS: ['mark'], ALLOWED_ATTR: [] };

/**
 * Render-ready HTML for a snippet — the only thing that may be handed to
 * `dangerouslySetInnerHTML`.
 *
 * `sanitizeSearchSnippet` above is the primary defence and is pinned by
 * `tests/unit/search-snippet.test.ts`. DOMPurify is a second, independent gate
 * at the injection point: it re-parses the result and drops anything that is
 * not a bare `<mark>`, so a bug in the hand-written escape/re-admit pass cannot
 * on its own put markup into the document.
 *
 * DOMPurify needs a DOM, which the server render does not have
 * (`isSupported === false`, and `sanitize` is not even defined there). On that
 * path the escaped string stands alone — which is safe, and unreachable in
 * practice because snippets only exist after a client-side fetch.
 */
export function purifySearchSnippet(snippet?: string | null): string {
  const escaped = sanitizeSearchSnippet(snippet);

  return DOMPurify.isSupported
    ? DOMPurify.sanitize(escaped, SNIPPET_PURIFY_CONFIG)
    : escaped;
}
